import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound, Copy, Search, Shield, User as UserIcon, BookOpen, FileDown, CheckCircle2, XCircle, ChevronsUpDown, Pencil, Route, FlaskConical } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Checkbox } from "../../components/ui/checkbox";
import AplActionsPanel from "../../components/AplActionsPanel";
import InstallmentPlanCard from "../../components/InstallmentPlanCard";
import LearningPathModal from "../../components/LearningPathModal";
import CartPricingSummary from "../../components/admin/CartPricingSummary";

const EMPTY_FORM = {
  first_name: "",
  last_name: "",
  dni: "",
  email: "",
  password: "",
  role: "alumno",
  phone: "",
  address: "",
  postal_code: "",
  city: "",
};

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const isSuperadmin = currentUser?.role === "superadmin";
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [credentialDialog, setCredentialDialog] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("alumno"); // alumno | staff | all
  const [coursesUser, setCoursesUser] = useState(null);
  const [coursesData, setCoursesData] = useState(null);
  const [allTests, setAllTests] = useState([]);
  const [courseSearch, setCourseSearch] = useState("");
  const [courseStatus, setCourseStatus] = useState("all"); // all | passed | inprogress | none
  const [bulkSelected, setBulkSelected] = useState([]);
  const [activePromos, setActivePromos] = useState([]);
  const [bulkSearch, setBulkSearch] = useState("");
  // Assigning with no receipt is a deliberate exception: confirm it, and let
  // the manager record why (note kept internal to the admin panel).
  const [assignConfirm, setAssignConfirm] = useState(false);
  const [assignStep, setAssignStep] = useState(1); // 1 = ask, 2 = write the note
  const [assignNote, setAssignNote] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [pathUser, setPathUser] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", dni: "", email: "", password: "" });
  const [permsUser, setPermsUser] = useState(null);
  const [permsForm, setPermsForm] = useState({ create_promotions: false, create_discount_codes: false, create_installment_plans: false, edit_course_price: false, view_invoices: false, view_tickets: false });
  const [permsSaving, setPermsSaving] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchUsers = () => {
    setLoading(true);
    // The "staff" tab groups gestion + admin + superadmin (all managers).
    // Backend accepts a single role filter, so for the staff tab we ask for
    // all rows and filter client-side.
    const query = roleFilter === "alumno" ? "?role=alumno" : "";
    api
      .get(`/admin/users${query}`)
      .then((r) => {
        const rows = r.data || [];
        setUsers(
          roleFilter === "staff"
            ? rows.filter((u) => u.role === "gestion" || u.role === "admin" || u.role === "superadmin")
            : rows,
        );
      })
      .finally(() => setLoading(false));
  };
  useEffect(fetchUsers, [roleFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.name, u.first_name, u.last_name, u.email, u.dni]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [users, search]);

  const createUser = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/users", {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        dni: form.dni.trim().toUpperCase(),
        email: form.email.trim().toLowerCase(),
        password: form.password || null,
        role: form.role,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        postal_code: form.postal_code.trim() || null,
        city: form.city.trim() || null,
      });
      setOpen(false);
      setForm(EMPTY_FORM);
      setCredentialDialog({ email: data.email, password: data.generated_password });
      fetchUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const regenerate = async (id, email) => {
    try {
      const { data } = await api.post(`/admin/users/${id}/regenerate-password`);
      setCredentialDialog({ email, password: data.generated_password });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("¿Eliminar este usuario? Sus intentos y asignaciones se eliminarán.")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      fetchUsers();
      toast.success("Usuario eliminado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado");
  };

  // Superadmin-only: a test account's tickets and invoices are numbered 0000
  // and never counted, so flagging the wrong person would silently stop their
  // real receipts from being counted. Hence the confirmation.
  const toggleTestAccount = async (u) => {
    const name = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email;
    const question = u.is_test
      ? `¿Dejar de usar a ${name} como cuenta de pruebas? A partir de ahora sus tickets y facturas llevarán numeración real y contarán en los totales.`
      : `¿Marcar a ${name} como cuenta de pruebas? Sus tickets y facturas pasarán a numerarse 0000 y no contarán en ningún total. Los documentos ya emitidos no cambian.`;
    if (!window.confirm(question)) return;
    try {
      await api.patch(`/admin/users/${u.id}/test-flag`, { is_test: !u.is_test });
      toast.success(u.is_test ? "Ya no es cuenta de pruebas" : "Marcada como cuenta de pruebas");
      fetchUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openCourses = async (user) => {
    setCoursesUser(user);
    setCoursesData(null);
    setCourseSearch("");
    setCourseStatus("all");
    setBulkSelected([]);
    setBulkSearch("");
    try {
      const [{ data: courses }, { data: tests }, promos] = await Promise.all([
        api.get(`/admin/users/${user.id}/courses`),
        api.get("/admin/courses"),
        // Packs and discounts shown in the picker. Non-blocking: if this
        // fails the picker still works, just without the offers.
        api.get("/admin/promotions/active").then((r) => r.data).catch(() => []),
      ]);
      setCoursesData(courses);
      setAllTests(tests);
      setActivePromos(promos);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setCoursesUser(null);
    }
  };

  // Supports "Ir al alumno" links from outside this page (e.g. the Cursos
  // "Asignados" modal): /admin/users?open=<userId> auto-opens that student's
  // "Cursos del usuario" dialog once the list has loaded. There's no
  // dedicated per-student route, so this is the plumbing that stands in for one.
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || users.length === 0) return;
    const u = users.find((x) => x.id === openId);
    if (u) openCourses(u);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("open");
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, searchParams]);

  const refreshCourses = async () => {
    if (!coursesUser) return;
    const { data } = await api.get(`/admin/users/${coursesUser.id}/courses`);
    setCoursesData(data);
  };

  const bulkAssign = async () => {
    if (!coursesUser || bulkSelected.length === 0) return;
    try {
      const { data } = await api.post("/admin/assignments/bulk", {
        user_id: coursesUser.id,
        course_ids: bulkSelected,
        note: assignNote.trim() || null,
      });
      toast.success(`${data.created} asignados${data.skipped ? ` · ${data.skipped} ya asignados` : ""}`);
      setBulkSelected([]);
      setAssignConfirm(false);
      setAssignNote("");
      await refreshCourses();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const unassignCourse = async (assignmentId, title) => {
    if (!window.confirm(`¿Desasignar "${title}"? Los intentos se mantendrán pero el usuario dejará de ver el curso.`)) return;
    try {
      await api.delete(`/admin/assignments/${assignmentId}`);
      toast.success("Curso desasignado");
      await refreshCourses();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const exportCoursesPdf = async () => {
    if (!coursesUser) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/users/${coursesUser.id}/courses/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo descargar el histórico");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = `${coursesUser.first_name || ""}_${coursesUser.last_name || ""}`.replace(/\s+/g, "_");
      a.download = `historico-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const exportConsolidatedPdf = async () => {
    if (!coursesUser) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/users/${coursesUser.id}/consolidated`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo generar el diploma consolidado");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = `${coursesUser.first_name || ""}_${coursesUser.last_name || ""}`.replace(/\s+/g, "_");
      a.download = `diploma-consolidado-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const downloadCertificate = async (courseId, title, isFinal = false) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/admin/users/${coursesUser.id}/courses/${courseId}/diploma`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || (isFinal ? "No se pudo descargar el diploma" : "No se pudo descargar el certificado"));
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${isFinal ? "diploma" : "certificado"}-${(title || "test").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("es-ES");
    } catch {
      return "—";
    }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setEditForm({
      first_name: u.first_name || "",
      last_name: u.last_name || "",
      dni: u.dni || "",
      email: u.email || "",
      password: "",
      phone: u.phone || "",
      address: (u.billing && u.billing.address) || "",
      postal_code: (u.billing && u.billing.postal_code) || "",
      city: (u.billing && u.billing.city) || "",
    });
  };

  useEffect(() => {
    if (permsUser) {
      const p = permsUser.perms || {};
      setPermsForm({
        create_promotions: !!p.create_promotions,
        create_discount_codes: !!p.create_discount_codes,
        create_installment_plans: !!p.create_installment_plans,
        edit_course_price: !!p.edit_course_price,
        view_invoices: !!p.view_invoices,
        view_tickets: !!p.view_tickets,
      });
    }
  }, [permsUser]);

  const savePerms = async () => {
    if (!permsUser) return;
    setPermsSaving(true);
    try {
      await api.put(`/admin/users/${permsUser.id}/gestion-perms`, permsForm);
      toast.success("Permisos actualizados");
      setPermsUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudieron guardar los permisos");
    } finally {
      setPermsSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editUser) return;
    const payload = {};
    if (editForm.first_name && editForm.first_name !== editUser.first_name)
      payload.first_name = editForm.first_name;
    if (editForm.last_name !== editUser.last_name) payload.last_name = editForm.last_name;
    if (editForm.dni && editForm.dni !== editUser.dni) payload.dni = editForm.dni.toUpperCase();
    if (editForm.email && editForm.email.toLowerCase() !== editUser.email)
      payload.email = editForm.email.toLowerCase();
    if (editForm.password) payload.password = editForm.password;
    const currentBilling = editUser.billing || {};
    if (editForm.phone !== (editUser.phone || "")) payload.phone = editForm.phone;
    if (editForm.address !== (currentBilling.address || "")) payload.address = editForm.address;
    if (editForm.postal_code !== (currentBilling.postal_code || "")) payload.postal_code = editForm.postal_code;
    if (editForm.city !== (currentBilling.city || "")) payload.city = editForm.city;
    if (Object.keys(payload).length === 0) {
      toast.info("No hay cambios que guardar");
      return;
    }
    try {
      const { data } = await api.put(`/admin/users/${editUser.id}`, payload);
      toast.success("Persona actualizada");
      setEditUser(null);
      fetchUsers();
      if (data.generated_password) {
        setCredentialDialog({ email: data.email, password: data.generated_password });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl text-gray-900">Personas usuarias</h1>
          <p className="text-sm text-gray-500 mt-1">
            Crea fichas de usuario y administradores. Asigna contraseñas.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand hover:bg-brand-hover text-white" data-testid="new-user-button">
              <Plus className="w-4 h-4 mr-2" /> Nueva persona usuaria
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Crear persona usuaria</DialogTitle>
            </DialogHeader>
            <form onSubmit={createUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="first_name">Nombre</Label>
                  <Input
                    id="first_name"
                    required
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                    data-testid="new-user-firstname"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Apellidos</Label>
                  <Input
                    id="last_name"
                    required
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                    data-testid="new-user-lastname"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="dni">DNI / NIE / Pasaporte</Label>
                <Input
                  id="dni"
                  required
                  value={form.dni}
                  onChange={(e) => setForm({ ...form, dni: e.target.value.toUpperCase() })}
                  placeholder="12345678A · X1234567L · AB1234567"
                  className="font-mono-data"
                  data-testid="new-user-dni"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="new-user-email"
                />
              </div>
              <div>
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Déjala vacía para generar automáticamente"
                  data-testid="new-user-password"
                />
              </div>
              <div>
                <Label htmlFor="role">Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="new-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alumno">Alumno/a</SelectItem>
                    {isSuperadmin && (
                      <>
                        <SelectItem value="gestion">Gestión</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="superadmin">Superadmin</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
                {!isSuperadmin && (
                  <p className="text-[11px] text-gray-400 mt-1">
                    Solo un superadmin puede crear cuentas de gestión, admin o superadmin.
                  </p>
                )}
              </div>

              {form.role === "alumno" && (
                <div className="pt-3 mt-2 border-t border-gray-100 space-y-3">
                  <p className="text-[11px] uppercase tracking-wider text-gray-500">
                    Datos de facturación (opcionales)
                  </p>
                  <p className="text-[11px] text-gray-400 -mt-2">
                    Se usarán automáticamente al generar la factura de este alumno.
                  </p>
                  <div>
                    <Label htmlFor="phone">Teléfono</Label>
                    <Input
                      id="phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+34 600 00 00 00"
                      data-testid="new-user-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address">Dirección</Label>
                    <Input
                      id="address"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      placeholder="Calle, número, piso…"
                      data-testid="new-user-address"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="postal_code">Código postal</Label>
                      <Input
                        id="postal_code"
                        value={form.postal_code}
                        onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                        placeholder="28001"
                        className="font-mono-data"
                        data-testid="new-user-postal-code"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">Localidad</Label>
                      <Input
                        id="city"
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                        placeholder="Madrid"
                        data-testid="new-user-city"
                      />
                    </div>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button type="submit" className="bg-brand hover:bg-brand-hover text-white" data-testid="new-user-submit">
                  Crear
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <Tabs value={roleFilter} onValueChange={setRoleFilter}>
          <TabsList>
            <TabsTrigger value="alumno" data-testid="tab-users"><UserIcon className="w-3.5 h-3.5 mr-1" />Alumnos/as</TabsTrigger>
            <TabsTrigger value="staff" data-testid="tab-admins"><Shield className="w-3.5 h-3.5 mr-1" />Gestión + Admin</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">Todos</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email, DNI/NIE/Pasaporte…"
            className="pl-9"
            data-testid="user-search"
          />
        </div>
      </div>

      <Card className="border border-gray-200 shadow-none rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nombre</TableHead>
              <TableHead>DNI / NIE / Pasaporte</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Creado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-gray-500 py-8">Cargando…</TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-gray-500 py-8">Sin resultados.</TableCell>
              </TableRow>
            ) : (
              filtered.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium">
                    {u.first_name || u.last_name ? `${u.first_name || ""} ${u.last_name || ""}`.trim() : u.name}
                    {u.is_test && (
                      <span
                        className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 align-middle"
                        title="Cuenta de pruebas: sus tickets y facturas se numeran 0000 y no computan"
                        data-testid={`test-badge-${u.email}`}
                      >
                        <FlaskConical className="w-3 h-3" /> PRUEBAS
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono-data text-sm">{u.dni || "—"}</TableCell>
                  <TableCell className="font-mono-data text-sm">{u.email}</TableCell>
                  <TableCell>
                    {u.role === "superadmin" ? (
                      <span className="inline-flex items-center gap-1 text-white bg-purple-600 px-2 py-0.5 rounded-full text-xs">
                        <Shield className="w-3 h-3" /> Superadmin
                      </span>
                    ) : u.role === "admin" ? (
                      <span className="inline-flex items-center gap-1 text-white bg-indigo-600 px-2 py-0.5 rounded-full text-xs">
                        <Shield className="w-3 h-3" /> Admin
                      </span>
                    ) : u.role === "gestion" ? (
                      <span className="inline-flex items-center gap-1 text-brand-ink bg-brand-soft px-2 py-0.5 rounded-full text-xs">
                        <Shield className="w-3 h-3" /> Gestión
                      </span>
                    ) : (
                      <span className="text-xs text-gray-600">Alumno/a</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-gray-500">
                    {new Date(u.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    {u.role === "alumno" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openCourses(u)}
                        data-testid={`courses-${u.email}`}
                      >
                        <BookOpen className="w-3.5 h-3.5 mr-1" />
                        Cursos
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(u)}
                      data-testid={`edit-${u.email}`}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => regenerate(u.id, u.email)}
                      data-testid={`regen-${u.email}`}
                    >
                      <KeyRound className="w-3.5 h-3.5 mr-1" />
                      Nueva contraseña
                    </Button>
                    {isSuperadmin && u.role === "alumno" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleTestAccount(u)}
                        data-testid={`test-toggle-${u.email}`}
                        title={u.is_test
                          ? "Dejar de usarla como cuenta de pruebas"
                          : "Marcar como cuenta de pruebas: sus documentos se numeran 0000 y no computan"}
                        className={u.is_test
                          ? "border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100"
                          : "border-gray-300 text-gray-500 hover:bg-gray-50"}
                      >
                        <FlaskConical className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {isSuperadmin && u.role === "gestion" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPermsUser(u)}
                        data-testid={`perms-${u.email}`}
                        className="border-purple-300 text-purple-700 hover:bg-purple-50"
                      >
                        <Shield className="w-3.5 h-3.5 mr-1" />
                        Permisos
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteUser(u.id)}
                      data-testid={`delete-${u.email}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!credentialDialog} onOpenChange={() => setCredentialDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Credenciales generadas</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Guarda esta contraseña ahora. No podrás verla de nuevo.
          </p>
          <div className="space-y-3 mt-2">
            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="font-mono-data text-sm">{credentialDialog?.email}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => copy(credentialDialog.email)}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center justify-between bg-brand-soft border border-[#bcdfd9] rounded-lg p-3">
              <div>
                <p className="text-xs text-brand-ink">Contraseña</p>
                <p className="font-mono-data text-base text-brand-ink">{credentialDialog?.password}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => copy(credentialDialog.password)} data-testid="copy-password">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setCredentialDialog(null)} data-testid="close-credentials">Entendido</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(o) => { if (!o) setEditUser(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar persona usuaria</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => { e.preventDefault(); saveEdit(); }}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit_first">Nombre</Label>
                <Input
                  id="edit_first"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                  data-testid="edit-firstname"
                />
              </div>
              <div>
                <Label htmlFor="edit_last">Apellidos</Label>
                <Input
                  id="edit_last"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                  data-testid="edit-lastname"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit_dni">DNI / NIE / Pasaporte</Label>
              <Input
                id="edit_dni"
                value={editForm.dni}
                onChange={(e) => setEditForm({ ...editForm, dni: e.target.value.toUpperCase() })}
                className="font-mono-data"
                data-testid="edit-dni"
              />
            </div>
            <div>
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                data-testid="edit-email"
              />
            </div>
            <div>
              <Label htmlFor="edit_password">Nueva contraseña (opcional)</Label>
              <Input
                id="edit_password"
                value={editForm.password}
                onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                placeholder="Déjala vacía para no modificarla"
                data-testid="edit-password"
              />
            </div>
            {editUser?.role === "alumno" && (
              <div className="pt-3 mt-2 border-t border-gray-100 space-y-3">
                <p className="text-[11px] uppercase tracking-wider text-gray-500">
                  Datos de facturación (opcionales)
                </p>
                <p className="text-[11px] text-gray-400 -mt-2">
                  Se usarán automáticamente al generar la factura de este alumno.
                </p>
                <div>
                  <Label htmlFor="edit_phone">Teléfono</Label>
                  <Input
                    id="edit_phone"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    placeholder="+34 600 00 00 00"
                    data-testid="edit-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_address">Dirección</Label>
                  <Input
                    id="edit_address"
                    value={editForm.address}
                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    placeholder="Calle, número, piso…"
                    data-testid="edit-address"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="edit_postal_code">Código postal</Label>
                    <Input
                      id="edit_postal_code"
                      value={editForm.postal_code}
                      onChange={(e) => setEditForm({ ...editForm, postal_code: e.target.value })}
                      className="font-mono-data"
                      data-testid="edit-postal-code"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit_city">Localidad</Label>
                    <Input
                      id="edit_city"
                      value={editForm.city}
                      onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                      data-testid="edit-city"
                    />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEditUser(null)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-brand hover:bg-brand-hover text-white" data-testid="edit-submit">
                Guardar cambios
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!coursesUser} onOpenChange={() => { setCoursesUser(null); setCoursesData(null); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="courses-dialog-title">
              Cursos de {coursesUser?.first_name || ""} {coursesUser?.last_name || ""}
              <span className="text-gray-400 font-normal text-sm ml-2">
                {coursesUser?.dni ? `· ${coursesUser.dni}` : ""}
              </span>
            </DialogTitle>
          </DialogHeader>

          {!coursesData ? (
            <p className="text-gray-500 text-sm py-6 text-center">Cargando…</p>
          ) : (
            <>
              {isSuperadmin && (
                <AplActionsPanel user={coursesUser} courses={coursesData.courses} />
              )}
              {coursesUser?.role === "alumno" && (
                <InstallmentPlanCard
                  user={coursesUser}
                  courses={coursesData.courses}
                  onPlanChanged={() => openCourses(coursesUser)}
                />
              )}
              {/* Counters + Export PDF */}
              {(() => {
                const total = coursesData.courses.length;
                const passed = coursesData.courses.filter((c) => c.passed).length;
                const inProgress = coursesData.courses.filter((c) => !c.passed && c.attempts > 0).length;
                const none = coursesData.courses.filter((c) => c.attempts === 0).length;
                return (
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap" data-testid="course-counters">
                      <span className="text-xs px-2.5 py-1 rounded-full border border-gray-200 bg-white">
                        Asignados: <span className="font-mono-data text-brand-ink">{total}</span>
                      </span>
                      <span className="text-xs px-2.5 py-1 rounded-full border border-[#bcdfd9] bg-brand-soft text-brand-ink">
                        Aprobados: <span className="font-mono-data">{passed}</span>
                      </span>
                      <span className="text-xs px-2.5 py-1 rounded-full border border-amber-200 bg-amber-50 text-amber-800">
                        En progreso: <span className="font-mono-data">{inProgress}</span>
                      </span>
                      <span className="text-xs px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-600">
                        Sin intentos: <span className="font-mono-data">{none}</span>
                      </span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPathUser(coursesUser)}
                        data-testid="admin-view-learning-path"
                      >
                        <Route className="w-3.5 h-3.5 mr-1" />
                        Ver trayecto
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={exportCoursesPdf}
                        data-testid="export-courses-pdf"
                      >
                        <FileDown className="w-3.5 h-3.5 mr-1" />
                        Exportar histórico PDF
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-[#e7d5b4] text-[#8a6f43] hover:bg-[#fdf6e6]"
                        onClick={exportConsolidatedPdf}
                        data-testid="export-consolidated-pdf"
                      >
                        <FileDown className="w-3.5 h-3.5 mr-1" />
                        Diploma consolidado
                      </Button>
                    </div>
                  </div>
                );
              })()}

              {/* Bulk assign */}
              {(() => {
                const assignedIds = new Set(coursesData.courses.map((c) => c.course_id));
                const available = allTests.filter((t) => !assignedIds.has(t.id));
                const q = bulkSearch.trim().toLowerCase();
                const filteredAvailable = q
                  ? available.filter((t) => (t.title || "").toLowerCase().includes(q))
                  : available;
                const toggle = (id) => {
                  setBulkSelected((sel) =>
                    sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]
                  );
                };
                // Active packs whose courses are all still assignable — selecting
                // one ticks every course it bundles, so the admin doesn't have
                // to remember which courses make up the offer.
                const availableIds = new Set(available.map((t) => t.id));
                const packs = (activePromos || [])
                  .filter((p) => p.kind === "package" && (p.course_ids || []).length > 0)
                  .map((p) => ({
                    ...p,
                    assignable: (p.course_ids || []).filter((cid) => availableIds.has(cid)),
                  }))
                  .filter((p) => p.assignable.length > 0);
                const packChecked = (pk) => pk.assignable.every((cid) => bulkSelected.includes(cid));
                const togglePack = (pk) => {
                  setBulkSelected((sel) =>
                    packChecked(pk)
                      ? sel.filter((id) => !pk.assignable.includes(id))
                      : Array.from(new Set([...sel, ...pk.assignable])),
                  );
                };
                // Courses that carry a per-course discount today, to flag them.
                const discountedIds = new Set(
                  (activePromos || []).filter((p) => p.kind === "course" && p.course_id).map((p) => p.course_id),
                );
                const allChecked = filteredAvailable.length > 0 && filteredAvailable.every((t) => bulkSelected.includes(t.id));
                const toggleAll = () => {
                  if (allChecked) {
                    setBulkSelected((sel) => sel.filter((id) => !filteredAvailable.find((t) => t.id === id)));
                  } else {
                    setBulkSelected((sel) => Array.from(new Set([...sel, ...filteredAvailable.map((t) => t.id)])));
                  }
                };
                return (
                  <div className="p-3 bg-brand-soft/60 border border-[#d9ece8] rounded-lg mt-3">
                   <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-xs">Asignar nuevos cursos (multi-selección)</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full justify-between font-normal"
                            data-testid="bulk-assign-trigger"
                          >
                            <span className="truncate">
                              {bulkSelected.length === 0
                                ? "Selecciona cursos…"
                                : `${bulkSelected.length} curso${bulkSelected.length === 1 ? "" : "s"} seleccionado${bulkSelected.length === 1 ? "" : "s"}`}
                            </span>
                            <ChevronsUpDown className="w-4 h-4 opacity-50 ml-2" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[420px] p-0" align="start">
                          <div className="p-2 border-b border-gray-100">
                            <div className="relative">
                              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <Input
                                value={bulkSearch}
                                onChange={(e) => setBulkSearch(e.target.value)}
                                placeholder="Buscar cursos…"
                                className="pl-8 h-9"
                                data-testid="bulk-assign-search"
                              />
                            </div>
                          </div>
                          {filteredAvailable.length === 0 && packs.length === 0 ? (
                            <p className="text-xs text-gray-500 px-3 py-4 text-center">
                              {available.length === 0
                                ? "Todos los cursos ya están asignados."
                                : "Ningún curso coincide."}
                            </p>
                          ) : (
                            <>
                              {filteredAvailable.length > 0 && (
                                <button
                                  type="button"
                                  onClick={toggleAll}
                                  className="w-full text-left text-xs px-3 py-2 text-brand-ink hover:bg-brand-soft border-b border-gray-100"
                                  data-testid="bulk-assign-select-all"
                                >
                                  {allChecked ? "Deseleccionar todos" : "Seleccionar todos"} ({filteredAvailable.length})
                                </button>
                              )}
                              <div className="max-h-64 overflow-y-auto py-1">
                                {packs.length > 0 && (
                                  <>
                                    <p className="px-3 pt-1 pb-1 text-[10px] uppercase tracking-wider text-purple-700 font-semibold">
                                      Packs de oferta activos
                                    </p>
                                    {packs.map((pk) => (
                                      <label
                                        key={pk.id}
                                        className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-purple-50"
                                        data-testid={`bulk-pack-${pk.name}`}
                                      >
                                        <Checkbox
                                          checked={packChecked(pk)}
                                          onCheckedChange={() => togglePack(pk)}
                                        />
                                        <span className="text-sm flex-1">
                                          📦 {pk.name}
                                          <span className="block text-[10px] text-gray-500">
                                            {pk.assignable.length} de {(pk.course_ids || []).length} cursos disponibles
                                          </span>
                                        </span>
                                        <span className="text-xs text-purple-800 font-mono-data">
                                          {pk.fixed_price != null ? `${pk.fixed_price} €` : ""}
                                        </span>
                                      </label>
                                    ))}
                                    <div className="border-t border-gray-100 my-1" />
                                    <p className="px-3 pb-1 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                                      Cursos sueltos
                                    </p>
                                  </>
                                )}
                                {filteredAvailable.map((t) => (
                                  <label
                                    key={t.id}
                                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50"
                                    data-testid={`bulk-option-${t.title}`}
                                  >
                                    <Checkbox
                                      checked={bulkSelected.includes(t.id)}
                                      onCheckedChange={() => toggle(t.id)}
                                    />
                                    <span className="text-sm flex-1">
                                      {t.title}
                                      {discountedIds.has(t.id) && (
                                        <span className="ml-1 text-[10px] text-emerald-700">· en oferta</span>
                                      )}
                                    </span>
                                    <span className="text-xs text-gray-400 font-mono-data">
                                      {t.exams?.length || 0} ex.
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </>
                          )}
                        </PopoverContent>
                      </Popover>
                    </div>
                    {/* Only offered once something is selected — same rule as
                        the payment buttons in the summary below. */}
                    {bulkSelected.length > 0 && (
                      <Button
                        onClick={() => { setAssignNote(""); setAssignStep(1); setAssignConfirm(true); }}
                        className="bg-brand hover:bg-brand-hover text-white"
                        data-testid="bulk-assign-button"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Asignar {bulkSelected.length}
                      </Button>
                    )}
                   </div>
                    <CartPricingSummary
                      courseIds={bulkSelected}
                      informational
                      userId={coursesUser.id}
                      onPaid={async () => {
                        setBulkSelected([]);
                        await refreshCourses();
                      }}
                    />

                    <Dialog open={assignConfirm} onOpenChange={(o) => !o && setAssignConfirm(false)}>
                      <DialogContent data-testid="assign-no-ticket-dialog">
                        <DialogHeader>
                          <DialogTitle>
                            ¿Seguro que quiere asignar {bulkSelected.length === 1 ? "este curso" : "estos cursos"} sin generar ticket?
                          </DialogTitle>
                          <DialogDescription>
                            {assignStep === 1
                              ? "No se emitirá ningún recibo ni quedará registrado ningún cobro. Si el alumno ha pagado, usa «Generar pago» o el botón de Sabadell."
                              : "Añade un concepto que explique por qué se entrega sin cobro. Se verá bajo el nombre del curso en esta ficha; el alumno no lo ve."}
                          </DialogDescription>
                        </DialogHeader>
                        {assignStep === 2 && (
                          <div>
                            <Label htmlFor="assign-note" className="text-sm">Concepto (opcional)</Label>
                            <Input
                              id="assign-note"
                              value={assignNote}
                              onChange={(e) => setAssignNote(e.target.value)}
                              placeholder="Cortesía, incluido en convenio, reposición…"
                              data-testid="assign-no-ticket-note"
                            />
                          </div>
                        )}
                        <DialogFooter>
                          {assignStep === 1 ? (
                            <>
                              <Button variant="ghost" onClick={() => setAssignConfirm(false)}>No</Button>
                              <Button
                                onClick={() => setAssignStep(2)}
                                className="bg-brand hover:bg-brand-hover text-white"
                                data-testid="assign-no-ticket-yes"
                              >
                                Sí, asignar sin ticket
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button variant="ghost" onClick={() => setAssignConfirm(false)}>Cancelar</Button>
                              <Button
                                onClick={bulkAssign}
                                className="bg-brand hover:bg-brand-hover text-white"
                                data-testid="assign-no-ticket-confirm"
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Asignar {bulkSelected.length}
                              </Button>
                            </>
                          )}
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                );
              })()}

              {/* Filters */}
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <div className="relative flex-1 min-w-60">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={courseSearch}
                    onChange={(e) => setCourseSearch(e.target.value)}
                    placeholder="Buscar curso…"
                    className="pl-9"
                    data-testid="courses-search"
                  />
                </div>
                <div className="w-52">
                  <Select value={courseStatus} onValueChange={setCourseStatus}>
                    <SelectTrigger data-testid="courses-status-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los estados</SelectItem>
                      <SelectItem value="passed">Aprobados</SelectItem>
                      <SelectItem value="inprogress">En progreso</SelectItem>
                      <SelectItem value="none">Sin intentos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {(() => {
                const q = courseSearch.trim().toLowerCase();
                const filtered = coursesData.courses.filter((c) => {
                  if (q && !(c.title || "").toLowerCase().includes(q)) return false;
                  if (courseStatus === "passed" && !c.passed) return false;
                  if (courseStatus === "inprogress" && (c.passed || c.attempts === 0)) return false;
                  if (courseStatus === "none" && c.attempts !== 0) return false;
                  return true;
                });
                if (coursesData.courses.length === 0) {
                  return <p className="text-gray-500 text-sm py-6 text-center">Sin cursos asignados todavía.</p>;
                }
                if (filtered.length === 0) {
                  return <p className="text-gray-500 text-sm py-6 text-center">Ningún curso coincide con el filtro.</p>;
                }
                return (
                  <div className="border border-gray-200 rounded-lg overflow-hidden mt-3">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Curso</TableHead>
                          <TableHead>Inicio</TableHead>
                          <TableHead>Fin</TableHead>
                          <TableHead>Acierto</TableHead>
                          <TableHead>Intentos</TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead className="text-right">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.map((c) => (
                          <TableRow key={c.course_id} data-testid={`course-row-${c.title}`}>
                            <TableCell className="font-medium">
                              {c.title}
                              {c.exams?.length > 1 && (
                                <span className="ml-2 text-[10px] text-gray-400 font-mono-data">
                                  {c.exams.length} exámenes
                                </span>
                              )}
                              {/* Why it was handed over with no receipt. Admin-only. */}
                              {c.note && (
                                <span
                                  className="block text-[11px] font-normal text-amber-700"
                                  data-testid={`course-note-${c.title}`}
                                >
                                  {c.note}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">{formatDate(c.started)}</TableCell>
                            <TableCell className="text-sm text-gray-600">{formatDate(c.finished)}</TableCell>
                            <TableCell className="font-mono-data">
                              {c.final == null ? "—" : `${c.final}%`}
                            </TableCell>
                            <TableCell className="font-mono-data">{c.attempts}</TableCell>
                            <TableCell>
                              {c.passed ? (
                                <span className="inline-flex items-center gap-1 text-emerald-600 text-xs">
                                  <CheckCircle2 className="w-3.5 h-3.5" /> Aprobado
                                </span>
                              ) : c.attempts > 0 ? (
                                <span className="inline-flex items-center gap-1 text-red-500 text-xs">
                                  <XCircle className="w-3.5 h-3.5" /> En progreso
                                </span>
                              ) : (
                                <span className="text-gray-400 text-xs">Sin intentos</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right space-x-1">
                              {c.passed && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                                  onClick={() => downloadCertificate(c.course_id, c.title, c.kind === "final")}
                                  data-testid={`admin-cert-${c.title}`}
                                >
                                  <FileDown className="w-3.5 h-3.5 mr-1" />
                                  {c.kind === "final" ? "Diploma" : "Certificado"}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => unassignCourse(c.assignment_id, c.title)}
                                data-testid={`unassign-${c.title}`}
                                title="Desasignar"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                );
              })()}
            </>
          )}
        </DialogContent>
      </Dialog>

      <LearningPathModal
        open={!!pathUser}
        onOpenChange={(v) => !v && setPathUser(null)}
        variant="admin"
        userId={pathUser?.id}
        userLabel={pathUser ? `${pathUser.first_name || ""} ${pathUser.last_name || ""}`.trim() : ""}
      />

      <Dialog open={!!permsUser} onOpenChange={(v) => !v && setPermsUser(null)}>
        <DialogContent data-testid="perms-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-700" />
              Permisos de gestión
            </DialogTitle>
          </DialogHeader>
          {permsUser && (
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                {permsUser.first_name} {permsUser.last_name} · <span className="font-mono-data">{permsUser.email}</span>
              </p>
              <p className="text-xs text-gray-500">
                Activa las capacidades opcionales para este perfil de gestión. Los roles admin y superadmin las tienen por defecto.
              </p>
              <label className="flex items-start gap-3 p-3 rounded-md border border-gray-200 cursor-pointer hover:bg-gray-50">
                <Checkbox
                  checked={permsForm.create_promotions}
                  onCheckedChange={(v) => setPermsForm({ ...permsForm, create_promotions: !!v })}
                  className="mt-0.5"
                  data-testid="perm-create-promotions"
                />
                <div>
                  <p className="text-sm font-medium text-brand-ink">Crear ofertas desde Ajustes → Promociones</p>
                  <p className="text-xs text-gray-500 mt-0.5">Permite crear/editar/eliminar promociones de la tienda.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-md border border-gray-200 cursor-pointer hover:bg-gray-50">
                <Checkbox
                  checked={permsForm.create_discount_codes}
                  onCheckedChange={(v) => setPermsForm({ ...permsForm, create_discount_codes: !!v })}
                  className="mt-0.5"
                  data-testid="perm-create-discount-codes"
                />
                <div>
                  <p className="text-sm font-medium text-brand-ink">Crear códigos descuento</p>
                  <p className="text-xs text-gray-500 mt-0.5">Da acceso a la sección de códigos y a su ciclo completo (crear, editar, desactivar).</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-md border border-gray-200 cursor-pointer hover:bg-gray-50">
                <Checkbox
                  checked={permsForm.create_installment_plans}
                  onCheckedChange={(v) => setPermsForm({ ...permsForm, create_installment_plans: !!v })}
                  className="mt-0.5"
                  data-testid="perm-create-installment-plans"
                />
                <div>
                  <p className="text-sm font-medium text-brand-ink">Crear pago aplazado en alumno</p>
                  <p className="text-xs text-gray-500 mt-0.5">Permite activar el pago aplazado desde la ficha del alumno (Cursos → Pago aplazado).</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-md border border-gray-200 cursor-pointer hover:bg-gray-50">
                <Checkbox
                  checked={permsForm.edit_course_price}
                  onCheckedChange={(v) => setPermsForm({ ...permsForm, edit_course_price: !!v })}
                  className="mt-0.5"
                  data-testid="perm-edit-course-price"
                />
                <div>
                  <p className="text-sm font-medium text-brand-ink">Editar precio de curso/taller</p>
                  <p className="text-xs text-gray-500 mt-0.5">Permite cambiar el precio al crear o editar un curso o taller. Sin este permiso, el campo aparece bloqueado.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-md border border-gray-200 cursor-pointer hover:bg-gray-50">
                <Checkbox
                  checked={permsForm.view_invoices}
                  onCheckedChange={(v) => setPermsForm({ ...permsForm, view_invoices: !!v })}
                  className="mt-0.5"
                  data-testid="perm-view-invoices"
                />
                <div>
                  <p className="text-sm font-medium text-brand-ink">Ver facturas emitidas</p>
                  <p className="text-xs text-gray-500 mt-0.5">Muestra las facturas (y las rectificativas) en el resumen mensual de Progreso, con su número, y permite descargar el PDF.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-md border border-gray-200 cursor-pointer hover:bg-gray-50">
                <Checkbox
                  checked={permsForm.view_tickets}
                  onCheckedChange={(v) => setPermsForm({ ...permsForm, view_tickets: !!v })}
                  className="mt-0.5"
                  data-testid="perm-view-tickets"
                />
                <div>
                  <p className="text-sm font-medium text-brand-ink">Ver tickets emitidos</p>
                  <p className="text-xs text-gray-500 mt-0.5">Muestra los tickets en el resumen mensual de Progreso, con su número, y permite descargar el PDF. Es independiente del permiso de facturas.</p>
                </div>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setPermsUser(null)}>Cancelar</Button>
                <Button
                  onClick={savePerms}
                  disabled={permsSaving}
                  className="bg-purple-700 hover:bg-purple-800 text-white"
                  data-testid="perms-save"
                >
                  {permsSaving ? "Guardando…" : "Guardar permisos"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
